/**
 * CLI Issue Filing
 *
 * `issue create` files a new issue directly (human or agent); `issue bump` appends
 * a sighting to an existing one. Both can link a workflow run (`--from-run`), which
 * attaches the run's fast video (preferred) + trace server-side, and both accept
 * structured evidence (`--code-ref`, `--sentry`, `--log-query`) that lands as the
 * occurrence's diagnostic evidence — the same shape the diagnostic agent emits.
 */

import fs from 'node:fs/promises';
import process from 'node:process';
import type { FailureDiagnosticEvidence, IssueLogSystem } from '@chatsdet/types/issues';
import { getArgValue, hasFlag, resolveAppUrl } from './auth.js';
import { apiRequest, getApplicationIdArg } from './cli-helpers.js';

type IssueSummaryLite = {
  id: string;
  title: string;
  severity: string;
  status?: string;
  category?: { label?: string } | null;
};

type IssueDetailEnvelope = {
  ok: boolean;
  error?: string;
  data?: { issue: IssueSummaryLite };
};

type IssueListEnvelope = {
  ok: boolean;
  error?: string;
  data?: { data?: IssueSummaryLite[] } | IssueSummaryLite[];
};

const ISSUE_LOG_SYSTEMS = ['sentry', 'cloudwatch', 'datadog', 'other'] as const;

const parseLogSystem = (value: string | undefined): IssueLogSystem => {
  if (!value) return 'other';
  const match = ISSUE_LOG_SYSTEMS.find((candidate) => candidate === value);
  return match ?? 'other';
};

/** Collect every value for a repeatable flag (getArgValue only returns the first). */
export function getArgValues(argv: string[], key: string): string[] {
  const out: string[] = [];
  for (let i = 0; i < argv.length; i += 1) {
    const arg = argv[i];
    if (arg === key && argv[i + 1] && !argv[i + 1]!.startsWith('-')) {
      out.push(argv[i + 1]!);
    } else if (arg?.startsWith(`${key}=`)) {
      out.push(arg.slice(key.length + 1));
    }
  }
  return out;
}

/** Parse `--code-ref` / `--sentry` / `--log-query` flags into evidence items. */
export function buildEvidence(argv: string[]): FailureDiagnosticEvidence[] {
  const evidence: FailureDiagnosticEvidence[] = [];
  const repo = getArgValue(argv, '--repo');
  const commit = getArgValue(argv, '--commit');

  for (const ref of getArgValues(argv, '--code-ref')) {
    // `path[:line]` — trailing `:<digits>` is the line number.
    const match = ref.match(/^(.*?):(\d+)$/);
    const path = match ? match[1] : ref;
    const line = match ? Number.parseInt(match[2], 10) : undefined;
    evidence.push({
      type: 'code_reference',
      description: ref,
      path,
      ...(line !== undefined ? { line } : {}),
      ...(repo ? { repo } : {}),
      ...(commit ? { commit } : {}),
    });
  }

  for (const value of getArgValues(argv, '--sentry')) {
    const isUrl = /^https?:\/\//.test(value);
    evidence.push({
      type: 'log_reference',
      logSystem: 'sentry',
      description: `Sentry ${value}`,
      ...(isUrl ? { url: value } : { logId: value }),
    });
  }

  const logSystem = parseLogSystem(getArgValue(argv, '--log-system'));
  for (const query of getArgValues(argv, '--log-query')) {
    evidence.push({ type: 'log_reference', logSystem, description: 'Log query', query });
  }

  return evidence;
}

/** Freeform report body: `--from-file <path>` (Markdown) or inline `--body`. */
export async function readBody(argv: string[]): Promise<string | undefined> {
  const fromFile = getArgValue(argv, '--from-file');
  if (fromFile) {
    try {
      return await fs.readFile(fromFile, 'utf8');
    } catch (err) {
      console.error(
        `Error: cannot read --from-file ${fromFile}: ${err instanceof Error ? err.message : String(err)}`
      );
      process.exit(1);
    }
  }
  return getArgValue(argv, '--body');
}

function printFiled(issue: IssueSummaryLite | undefined, apiUrl: string, runId?: string): void {
  const appUrl = resolveAppUrl(apiUrl);
  console.log('✓ Issue filed');
  console.log(`  ID:        ${issue?.id ?? '(unknown)'}`);
  if (issue?.title) console.log(`  Title:     ${issue.title}`);
  if (issue?.severity) console.log(`  Severity:  ${issue.severity}`);
  if (issue?.category?.label) console.log(`  Category:  ${issue.category.label}`);
  if (runId) console.log(`  From run:  ${runId} (fast video attached when available)`);
  console.log(`  Issues:    ${appUrl}/issues`);
  if (issue?.id) console.log(`  Detail:    cofactor issues get ${issue.id}`);
}

export async function handleIssueCreate(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const jsonOutput = hasFlag(argv, '--json');
  const title = getArgValue(argv, '--title');
  const propertyId = getApplicationIdArg(argv);
  const url = getArgValue(argv, '--url');
  const runId = getArgValue(argv, '--from-run');
  const severity = getArgValue(argv, '--severity') ?? 'medium';
  const category = getArgValue(argv, '--category') ?? 'business_logic_failure';

  if (!title) {
    console.error('Error: Missing --title.');
    console.error('Usage: cofactor issue create --title <t> --application-id <uuid> --url <url> [options]');
    process.exit(1);
  }
  if (!propertyId) {
    console.error('Error: Missing --application-id (alias --property-id).');
    process.exit(1);
  }

  // Search-first duplicate check: list similar issues and stop (do not file).
  if (hasFlag(argv, '--check')) {
    const search = await apiRequest<IssueListEnvelope>(
      apiUrl,
      token,
      'GET',
      `/v2/issues?search=${encodeURIComponent(title)}&pageSize=10`
    );
    if (!search.ok) {
      console.error(`Error: ${search.error}`);
      process.exit(1);
    }
    const rows = Array.isArray(search.data)
      ? search.data
      : (search.data?.data ?? []);
    if (rows.length === 0) {
      console.log(`No existing issues match "${title}". Re-run without --check to file it.`);
    } else {
      console.log(`Possible duplicates for "${title}":`);
      for (const row of rows) {
        console.log(`  ${row.id}  [${row.severity}${row.status ? `/${row.status}` : ''}]  ${row.title}`);
      }
      console.log('');
      console.log('Re-run without --check to file a new issue, or add a sighting to an');
      console.log('existing one:  cofactor issue bump <id> --url <url>');
    }
    return;
  }

  if (!url) {
    console.error('Error: Missing --url (the page where the bug lives).');
    process.exit(1);
  }

  const body = await readBody(argv);
  const evidence = buildEvidence(argv);

  const payload: Record<string, unknown> = {
    propertyId,
    issueTitle: title,
    issueSeverity: severity,
    issueCategoryKey: category,
    primaryUrl: url,
    ...(body ? { body } : {}),
    ...(runId ? { runId } : {}),
    ...(evidence.length ? { evidence } : {}),
  };

  const result = await apiRequest<IssueDetailEnvelope>(apiUrl, token, 'POST', '/v2/issues', payload);

  if (!result.ok) {
    if (jsonOutput) {
      console.log(JSON.stringify({ ok: false, error: result.error }, null, 2));
    } else {
      console.error(`Error: ${result.error}`);
    }
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  printFiled(result.data?.issue, apiUrl, runId);
}

export async function handleIssueBump(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const jsonOutput = hasFlag(argv, '--json');
  const issueId = argv[0];
  if (!issueId || issueId.startsWith('--')) {
    console.error('Error: Missing issue ID.');
    console.error('Usage: cofactor issue bump <issueId> --url <url> [--from-run <runId>] [options]');
    process.exit(1);
  }

  const url = getArgValue(argv, '--url');
  if (!url) {
    console.error('Error: Missing --url (the page where the bug was seen again).');
    process.exit(1);
  }

  const runId = getArgValue(argv, '--from-run');
  const body = await readBody(argv);
  const evidence = buildEvidence(argv);

  const payload: Record<string, unknown> = {
    primaryUrl: url,
    ...(body ? { body } : {}),
    ...(runId ? { runId } : {}),
    ...(evidence.length ? { evidence } : {}),
  };

  const result = await apiRequest<IssueDetailEnvelope>(
    apiUrl,
    token,
    'POST',
    `/v2/issues/${issueId}/occurrences`,
    payload
  );

  if (!result.ok) {
    if (jsonOutput) {
      console.log(JSON.stringify({ ok: false, error: result.error }, null, 2));
    } else {
      console.error(`Error: ${result.error}`);
    }
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  console.log(`✓ Added a sighting to issue ${issueId}.`);
  if (runId) console.log(`  From run:  ${runId} (fast video attached when available)`);
  console.log(`  Detail:    cofactor issues get ${issueId}`);
}
